import React, { useState } from "react";
import {
  AlertOctagon, CheckCircle2, ChevronDown, ChevronRight, Lightbulb, Sparkles,
} from "lucide-react";
import { fmtMoney, fmtPct, fmtNum } from "./_utils";

// ---------- Top Denial Intelligence Trends (executive AI cards) ----------
/**
 * Premium executive trend cards (replaces the legacy "Categories Over Time" chart):
 *  - 5 AI-driven driver cards sorted by denied $
 *  - Each card carries: top ANSI/CARC code, driver, denied $, claim count, MoM trend pill, top payor, recovery opportunity, and a rule-based AI operational insight
 *  - Server-side filters "Unclassified — Manual Review" out of the list
 */
const TrendPill = ({ trend }) => {
  if (!trend) return null;
  const dir = trend.direction || "flat";
  const delta = Math.abs(trend.delta_pct || 0);
  if (dir === "flat" || delta < 1) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider"
        style={{ background: "rgba(148,163,184,0.15)", border: "1px solid rgba(148,163,184,0.30)", color: "#CBD5E1" }}
        title="Compared to prior rolling 30-day period"
      >
        → Flat MoM
      </span>
    );
  }
  // UP = more denials = bad (red); DOWN = fewer denials = good (teal/emerald)
  const up = dir === "up";
  const palette = up
    ? { bg: "rgba(239,68,68,0.12)",  border: "rgba(239,68,68,0.45)",  color: "#FCA5A5", arrow: "↑" }
    : { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.45)", color: "#6EE7B7", arrow: "↓" };
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md uppercase tracking-wider"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.color }}
      title="Compared to prior rolling 30-day period"
    >
      <span>{palette.arrow}</span> {delta.toFixed(1)}% MoM
    </span>
  );
};

const RISK_TONE = {
  High:   { color: "#FCA5A5", bg: "rgba(239,68,68,0.10)", border: "rgba(239,68,68,0.45)" },
  Medium: { color: "#FCD34D", bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.45)" },
  Low:    { color: "#6EE7B7", bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.40)" },
};

const DenialIntelligenceTrendsPanel = ({ trends = [], onSelect = null }) => {
  const [activeIdx, setActiveIdx] = useState(null);
  const [open, setOpen] = useState(false);
  if (!trends || trends.length === 0) {
    return (
      <div className="ai-shell rounded-2xl p-6 lg:col-span-2" data-testid="denial-intelligence-trends">
        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
          ANSI Denial Intelligence
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5 text-white">
          Top 5 ANSI Denial Codes
        </h2>
        <div className="text-xs text-slate-400 mt-3">No ANSI denial codes available yet.</div>
      </div>
    );
  }
  const activeCode = activeIdx != null ? trends[activeIdx] : null;
  // Headline metrics — visible even when the section is collapsed.
  const totalDenied = trends.reduce((acc, t) => acc + (t.dollars || 0), 0);
  const totalRecoverable = trends.reduce((acc, t) => acc + (t.estimated_recoverable || 0), 0);
  const highRiskCount = trends.filter((t) => t.operational_risk === "High").length;

  return (
    <div className="ai-shell rounded-2xl lg:col-span-2 relative overflow-hidden" data-testid="denial-intelligence-trends">
      {/* Collapsible header — clickable */}
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); if (open) setActiveIdx(null); }}
        aria-expanded={open}
        data-testid="denial-intelligence-toggle"
        className="w-full text-left p-6 hover:bg-white/[0.02] focus:outline-none focus-visible:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-cyan-300">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-70 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-300" />
              </span>
              ANSI Denial Intelligence
            </div>
            <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight mt-1.5 text-white">
              Top 5 ANSI Denial Codes
            </h2>
            <div className="text-xs text-slate-300 mt-1 font-medium">
              Ranked by financial impact, recoverability and denial concentration
            </div>
            {/* Inline summary chips — give value even while collapsed */}
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(20,184,166,0.10)", color: "#5EEAD4", border: "1px solid rgba(94,234,212,0.30)" }}>
                {trends.length} codes · {fmtMoney(totalDenied)} denied
              </span>
              <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(74,222,128,0.10)", color: "#86EFAC", border: "1px solid rgba(74,222,128,0.30)" }}>
                {fmtMoney(totalRecoverable)} est. recoverable
              </span>
              {highRiskCount > 0 && (
                <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: "rgba(248,113,113,0.12)", color: "#FCA5A5", border: "1px solid rgba(248,113,113,0.40)" }}>
                  {highRiskCount} high-risk
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider"
              style={{ background: "rgba(20,184,166,0.12)", border: "1px solid rgba(20,184,166,0.45)", color: "#5EEAD4" }}>
              <Sparkles className="h-3 w-3" /> Live
            </span>
            <span
              className="h-8 w-8 rounded-full flex items-center justify-center transition-transform duration-300"
              style={{ background: "rgba(20,184,166,0.12)", border: "1px solid rgba(94,234,212,0.30)", transform: open ? "rotate(180deg)" : "rotate(0)" }}
            >
              <ChevronDown className="h-4 w-4 text-teal-300" />
            </span>
          </div>
        </div>
      </button>

      {/* Collapsible body */}
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-500 ease-out"
        style={{ maxHeight: open ? "3000px" : "0", opacity: open ? 1 : 0 }}
      >
        <div className="px-6 pb-6 border-t border-white/[0.06]" data-testid="denial-intelligence-body">

      {/* Cards */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5 mt-5">
        {trends.map((t, i) => {
          const isActive = activeIdx === i;
          const riskTone = RISK_TONE[t.operational_risk] || RISK_TONE.Medium;
          return (
            <div
              key={t.display}
              data-testid={`denial-trend-card-${i}`}
              role="button"
              tabIndex={0}
              onClick={() => setActiveIdx(isActive ? null : i)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setActiveIdx(isActive ? null : i); } }}
              className={`ai-card relative rounded-xl p-4 flex flex-col gap-3 transition-all duration-300 cursor-pointer hover:-translate-y-1 hover:shadow-[0_0_24px_-6px_rgba(20,184,166,0.55)] hover:ring-1 hover:ring-teal-300/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/70 ${
                isActive ? "ring-2 ring-teal-400/70 shadow-[0_0_32px_-6px_rgba(20,184,166,0.85)]" : ""
              }`}
            >
              {/* Top row: code + trend */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span
                    className="inline-flex items-center font-display text-sm font-bold px-2 py-1 rounded-md tracking-wide"
                    style={{ background: "rgba(20,184,166,0.18)", border: "1px solid rgba(20,184,166,0.55)", color: "#5EEAD4" }}
                    data-testid={`denial-trend-code-${i}`}
                    title={t.known ? `ANSI ${t.display}` : `Unknown CARC ${t.code}`}
                  >
                    {t.display}
                  </span>
                  <span className="text-[10px] font-semibold text-slate-400">#{i + 1}</span>
                </div>
                <TrendPill trend={t.trend} />
              </div>

              {/* Title + ANSI description */}
              <div>
                {t.short_label && (
                  <div className="font-display text-base font-semibold text-white leading-tight mb-1" data-testid={`denial-trend-short-${i}`}>
                    {t.short_label}
                  </div>
                )}
                <p
                  className="text-[11.5px] leading-snug text-slate-300 line-clamp-2"
                  title={t.description}
                  data-testid={`denial-trend-description-${i}`}
                >
                  {t.description}
                </p>
              </div>

              {/* Dollars + count */}
              <div className="border-t border-white/8 pt-3">
                <div className="font-display text-2xl font-semibold text-white tracking-tight tabular-nums" data-testid={`denial-trend-dollars-${i}`}>
                  {fmtMoney(t.dollars)}
                </div>
                <div className="text-[11px] text-slate-300 mt-0.5 font-medium" data-testid={`denial-trend-count-${i}`}>
                  {fmtNum(t.count)} claims · {fmtPct(t.pct_of_total)} of denied $
                </div>
              </div>

              {/* Operational intelligence trio */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg px-2.5 py-1.5"
                  style={{ background: "rgba(94,234,212,0.08)", border: "1px solid rgba(94,234,212,0.25)" }}>
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-teal-200">Recovery Prob.</div>
                  <div className="font-display text-sm font-semibold tabular-nums" style={{ color: "#5EEAD4" }}>
                    {(t.recovery_probability || 0).toFixed(0)}%
                  </div>
                </div>
                <div className="rounded-lg px-2.5 py-1.5"
                  style={{ background: riskTone.bg, border: `1px solid ${riskTone.border}` }}>
                  <div className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: riskTone.color }}>Risk</div>
                  <div className="font-display text-sm font-semibold" style={{ color: riskTone.color }}>
                    {t.operational_risk || "—"}
                  </div>
                </div>
                <div className="rounded-lg px-2.5 py-1.5 col-span-2"
                  style={{ background: "rgba(94,234,212,0.05)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="text-[9px] uppercase tracking-wider font-semibold text-slate-300">Est. Recoverable</div>
                  <div className="font-display text-sm font-semibold tabular-nums text-white">
                    {fmtMoney(t.estimated_recoverable || 0)}
                  </div>
                </div>
              </div>

              {/* AI Insight */}
              {t.ai_insight && (
                <div className="rounded-lg p-2.5"
                  style={{
                    border: "1px solid rgba(94,234,212,0.32)",
                    background: "linear-gradient(160deg, rgba(20,184,166,0.10) 0%, rgba(20,184,166,0.04) 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                  }}
                >
                  <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] font-semibold text-cyan-200">
                    <Sparkles className="h-2.5 w-2.5" />
                    Insight
                  </div>
                  <p className="text-[11.5px] text-slate-100 leading-snug mt-1 font-medium">{t.ai_insight}</p>
                </div>
              )}

              {/* Highest concentration */}
              {t.top_payor && (
                <div className="text-[11px] flex items-center justify-between mt-auto pt-2 border-t border-white/8">
                  <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Highest Concentration</span>
                  <span className="text-slate-100 font-semibold truncate ml-2" title={t.top_payor}>
                    {t.top_payor}
                    {t.top_payor_share_pct > 0 && (
                      <span className="text-teal-300 ml-1 tabular-nums">· {Math.round(t.top_payor_share_pct)}%</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Expandable detail panel — appears below grid when a card is active */}
      {activeCode && (
        <div
          className="mt-5 rounded-2xl p-5 sm:p-6 relative animate-in fade-in-0 slide-in-from-top-2 duration-300"
          data-testid={`denial-trend-detail-${activeIdx}`}
          style={{
            background: "linear-gradient(160deg, rgba(20,184,166,0.06) 0%, rgba(15,23,42,0.5) 100%)",
            border: "1px solid rgba(94,234,212,0.30)",
            boxShadow: "0 0 40px -15px rgba(20,184,166,0.45)",
          }}
        >
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
            <div className="flex items-start gap-3">
              <span
                className="inline-flex items-center font-display text-base font-bold px-2.5 py-1.5 rounded-md tracking-wide shrink-0"
                style={{ background: "rgba(20,184,166,0.22)", border: "1px solid rgba(20,184,166,0.65)", color: "#5EEAD4" }}
              >
                {activeCode.display}
              </span>
              <div className="min-w-0">
                <div className="font-display text-base sm:text-lg font-semibold text-white leading-tight">
                  {activeCode.short_label || activeCode.description}
                </div>
                <div className="text-[11.5px] text-slate-300 mt-1 leading-snug max-w-3xl">
                  {activeCode.description}
                </div>
              </div>
            </div>
            <button
              onClick={() => setActiveIdx(null)}
              className="text-[11px] inline-flex items-center gap-1 text-teal-300 hover:text-teal-200 transition-colors shrink-0"
              data-testid={`denial-trend-detail-close-${activeIdx}`}
            >
              Collapse <ChevronDown className="h-3.5 w-3.5 rotate-180" />
            </button>
          </div>

          {/* 3-column body: Recoverability · Payor Concentration · Prevention */}
          <div className="grid lg:grid-cols-3 gap-3">
            {/* Recoverability scoring */}
            <div className="rounded-xl p-4" style={{ background: "rgba(11,21,36,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold mb-3" style={{ color: "#5EEAD4" }}>
                <Sparkles className="h-3 w-3" /> Recoverability Scoring
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11.5px] text-slate-300">Recovery Probability</span>
                  <span className="font-display text-sm font-semibold tabular-nums" style={{ color: "#5EEAD4" }}>
                    {(activeCode.recovery_probability || 0).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11.5px] text-slate-300">Estimated Recoverable</span>
                  <span className="font-display text-sm font-semibold tabular-nums text-white">
                    {fmtMoney(activeCode.estimated_recoverable || 0)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11.5px] text-slate-300">Preventability</span>
                  <span className="font-display text-sm font-semibold" style={{
                    color: activeCode.preventability_score === "High" ? "#FCD34D" :
                           activeCode.preventability_score === "Medium" ? "#5EEAD4" : "#86EFAC",
                  }}>
                    {activeCode.preventability_score || "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11.5px] text-slate-300">Operational Risk</span>
                  <span className="font-display text-sm font-semibold" style={{
                    color: activeCode.operational_risk === "High" ? "#FCA5A5" :
                           activeCode.operational_risk === "Medium" ? "#FCD34D" : "#86EFAC",
                  }}>
                    {activeCode.operational_risk || "—"}
                  </span>
                </div>
                <div className="pt-2.5 border-t border-white/5 flex items-center justify-between gap-3">
                  <span className="text-[11.5px] text-slate-300">Total Denied</span>
                  <span className="font-display text-sm font-semibold tabular-nums text-white">{fmtMoney(activeCode.dollars)}</span>
                </div>
              </div>
            </div>

            {/* Payor concentration breakdown */}
            <div className="rounded-xl p-4" style={{ background: "rgba(11,21,36,0.6)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold mb-3" style={{ color: "#5EEAD4" }}>
                <AlertOctagon className="h-3 w-3" /> Payor Concentration
              </div>
              {Array.isArray(activeCode.top_payors) && activeCode.top_payors.length > 0 ? (
                <div className="space-y-3">
                  {activeCode.top_payors.map((p, pi) => (
                    <div key={pi} data-testid={`denial-trend-detail-payor-${activeIdx}-${pi}`}>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="inline-flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-bold shrink-0"
                                style={{ background: "rgba(20,184,166,0.18)", color: "#5EEAD4", border: "1px solid rgba(20,184,166,0.55)" }}>
                            {pi + 1}
                          </span>
                          <span className="text-[12.5px] font-semibold text-white truncate" title={p.payor}>{p.payor}</span>
                        </div>
                        <span className="text-[11.5px] font-mono font-semibold tabular-nums text-teal-300 shrink-0">{fmtMoney(p.dollars)}</span>
                      </div>
                      <div className="h-1 w-full rounded-full overflow-hidden bg-slate-800/80">
                        <div className="h-full rounded-full transition-all duration-500"
                             style={{ width: `${Math.min(100, p.share_pct).toFixed(1)}%`, background: "linear-gradient(90deg, #14B8A6, #5EEAD4)" }} />
                      </div>
                      <div className="text-[10.5px] text-slate-400 mt-1 tabular-nums">{p.share_pct.toFixed(1)}% of this code's denied $</div>
                    </div>
                  ))}
                </div>
              ) : activeCode.top_payor ? (
                <div className="text-[12px] text-slate-200">
                  Concentrated with <span className="text-white font-semibold">{activeCode.top_payor}</span>
                  {activeCode.top_payor_share_pct > 0 && (
                    <span className="text-teal-300 ml-1">· {Math.round(activeCode.top_payor_share_pct)}%</span>
                  )}
                </div>
              ) : (
                <div className="text-[11.5px] text-slate-400 italic">No payor breakdown available.</div>
              )}
            </div>

            {/* Prevention recommendations + suggested workflows */}
            <div className="rounded-xl p-4" style={{ background: "rgba(11,21,36,0.6)", border: "1px solid rgba(94,234,212,0.30)" }}>
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold mb-3" style={{ color: "#5EEAD4" }}>
                <Lightbulb className="h-3 w-3" /> Prevention & Workflow
              </div>
              {activeCode.ai_insight && (
                <p className="text-[12px] leading-relaxed text-slate-100 mb-3">{activeCode.ai_insight}</p>
              )}
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-1.5">Suggested Workflows</div>
              <ul className="space-y-1.5 mb-3">
                {[
                  "Add pre-bill QA edit for this CARC pattern",
                  `Route ${activeCode.top_payor || "top payor"} accounts to escalation queue`,
                  "Track resolution rate weekly in operational dashboard",
                ].map((line, li) => (
                  <li key={li} className="flex items-start gap-2 text-[11.5px] text-slate-200">
                    <CheckCircle2 className="h-3 w-3 shrink-0 mt-0.5" style={{ color: "#5EEAD4" }} />
                    <span className="leading-snug">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400 mb-1.5 pt-2.5 border-t border-white/5">Appeal Guidance</div>
              <p className="text-[11.5px] leading-snug text-slate-200">
                Build a {activeCode.code ? `CO-${activeCode.code}` : activeCode.display}-specific appeal template citing the denial code, attach payor LCD/NCD criteria or auth approval evidence, and reference the original submission timestamps for timely-filing defenses.
              </p>
              {onSelect && (
                <button
                  onClick={(e) => { e.stopPropagation(); onSelect(activeCode); }}
                  className="mt-3 inline-flex items-center gap-1 text-[11.5px] font-semibold text-teal-300 hover:text-teal-200 transition-colors"
                  data-testid={`denial-trend-detail-cta-${activeIdx}`}
                >
                  Open Work Queue for this code <ChevronRight className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Footer interaction hint */}
      {!activeCode && (
        <div className="mt-4 text-[11px] text-slate-400 italic flex items-center gap-1.5">
          <Sparkles className="h-2.5 w-2.5 text-cyan-300" />
          Click any code to reveal full denial intelligence — recoverability scoring, payor concentration, prevention workflows, and AI appeal guidance.
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export { DenialIntelligenceTrendsPanel };
export default DenialIntelligenceTrendsPanel;
